import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Landmark,
  Cpu,
  MapPin,
  Download,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

// Students in the classroom, collaborating, coding & building projects.
const HERO_SLIDES = [
  {
    src: "/partners/smru/Hero2.jpeg",
    alt: "SMRU students working together",
  },
  {
    src: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=1600&auto=format&fit=crop",
    alt: "Students collaborating on a project",
    pos: "center",
  },
  {
    src: "https://images.unsplash.com/photo-1571260899304-425eee4c7efc?q=80&w=1600&auto=format&fit=crop",
    alt: "Student carrying textbooks on campus",
    pos: "center top",
  },


  {
    src: "/partners/smru/Hero1.jpeg",
    alt: "Students studying as a group",
  },
];

const FALLBACK_IMG = "/partners/smru/SMRU_COLLEGE.jpeg";

export default function SmruHero() {
  const [slide, setSlide] = useState(0);
  const count = HERO_SLIDES.length;

  const go = useCallback(
    (dir) => setSlide((s) => (s + dir + count) % count),
    [count],
  );

  // Auto-advance every 5s (resets whenever `slide` changes via manual nav too).
  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % count), 5000);
    return () => clearInterval(t);
  }, [slide, count]);

  const trust = [
    {
      Icon: Landmark,
      line1: "UGC 2(f) Recognized",
      line2: "St. Mary's University",
    },
    {
      Icon: Cpu,
      line1: "AI.Prof Ecosystem",
      line2: "India's First AI B.Tech",
    },
    {
      Icon: MapPin,
      line1: "Near Ramoji Film City",
      line2: "Deshmukhi, Hyderabad",
    },
  ];

  return (
    <section
      id="top"
      className="relative min-h-screen flex items-start overflow-hidden pt-12 sm:pt-16 pb-16"
    >
      {/* ── Student image carousel background (auto + manual) ───────────── */}
      <div className="absolute inset-0">
        <AnimatePresence>
          <motion.img
            key={slide}
            src={HERO_SLIDES[slide].src}
            alt={HERO_SLIDES[slide].alt}
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ opacity: { duration: 1 }, scale: { duration: 6, ease: "easeOut" } }}
            onError={(e) => {
              if (e.currentTarget.src !== window.location.origin + FALLBACK_IMG)
                e.currentTarget.src = FALLBACK_IMG;
            }}
            style={{ objectPosition: HERO_SLIDES[slide].pos || "center" }}
            className="absolute inset-0 h-full w-full object-cover"
          />
        </AnimatePresence>
        {/* Readability scrim — darker on the left where the text sits. */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/45 to-black/20" />
        {/* Soft bottom fade for depth and to blend into the section below. */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
      </div>

      {/* ── Carousel controls ───────────────────────────────────────────── */}
      {/* Dots (bottom-center) */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2.5">
        {HERO_SLIDES.map((s, i) => (
          <button
            key={s.src}
            onClick={() => setSlide(i)}
            aria-label={`Go to slide ${i + 1}`}
            className={`h-2.5 rounded-full transition-all ${
              i === slide ? "w-8 bg-aiorange" : "w-2.5 bg-white/50 hover:bg-white/80"
            }`}
          />
        ))}
      </div>
      {/* Prev / Next (bottom-right) */}
      <div className="absolute bottom-5 right-4 sm:right-8 z-20 flex items-center gap-2">
        <button
          onClick={() => go(-1)}
          aria-label="Previous slide"
          className="h-10 w-10 rounded-full bg-white/15 ring-1 ring-white/30 backdrop-blur-sm text-white flex items-center justify-center hover:bg-white/30 transition"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button
          onClick={() => go(1)}
          aria-label="Next slide"
          className="h-10 w-10 rounded-full bg-white/15 ring-1 ring-white/30 backdrop-blur-sm text-white flex items-center justify-center hover:bg-white/30 transition"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      {/* ── AI.Prof animated logo (top-right) ───────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="absolute top-2 right-4 sm:top-3 sm:right-10 z-20"
      >
        <img
          src="/ai_prof.png"
          alt="AI.Prof"
          className="h-16 sm:h-24 w-auto object-contain"
        />
      </motion.div>

      {/* ── Hero content (placed over the campus image) ─────────────────── */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 w-full">
        <div className="max-w-3xl text-white">
          {/* Urgency pill */}
        
          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-4 font-display font-black tracking-tight leading-[1.05] drop-shadow-[0_2px_14px_rgba(0,0,0,0.55)]"
          >
            <span className="block text-white text-3xl sm:text-4xl lg:text-5xl">
              India's First
            </span>
            <span className="block bg-gradient-to-r from-[#4D8BFF] to-[#FF8A3D] bg-clip-text text-transparent text-4xl sm:text-6xl lg:text-7xl mt-1">
              AI-Powered B.Tech
            </span>
            <span className="block text-amber-300 text-2xl sm:text-3xl lg:text-4xl mt-2">
              at St. Mary's University(SMRU), Hyderabad
            </span>
          </motion.h1>

          {/* Subhead */}
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="mt-6 text-lg text-white/90 max-w-xl drop-shadow-[0_1px_8px_rgba(0,0,0,0.5)]"
          >
            A 4-year career ecosystem combining SMRU's academic legacy
            with AI.Prof's industry-powered training built for
            students going global.
          </motion.p>

          {/* CTA pair */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.33 }}
            className="mt-8 flex flex-wrap gap-3"
          >
            <Link
              to="/RFC/guidance" target="_blank" rel="noopener noreferrer"
              className="cta-pulse inline-flex items-center gap-2 bg-aiblue hover:bg-blue-700 text-white rounded-full px-7 py-3.5 font-semibold transition-colors"
            >
              <ArrowRight className="w-5 h-5" />
              Get Guidance
            </Link>
            <Link
              to="/RFC/brochure" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border-2 border-white/50 text-white rounded-full px-7 py-3.5 font-semibold hover:bg-white hover:text-[#001A5C] transition-colors"
            >
              <Download className="w-5 h-5" />
              Download Brochure
            </Link>
          </motion.div>

          {/* Trust strip */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.42 }}
            className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl drop-shadow-[0_1px_8px_rgba(0,0,0,0.5)]"
          >
            {trust.map(({ Icon, line1, line2 }) => (
              <div key={line1} className="flex items-center gap-3">
                <span className="flex-shrink-0 h-10 w-10 rounded-full bg-black/30 ring-1 ring-white/20 backdrop-blur-sm flex items-center justify-center">
                  <Icon className="w-5 h-5 text-amber-300" />
                </span>
                <span className="text-sm leading-tight">
                  <span className="block font-semibold text-white">{line1}</span>
                  <span className="block text-white/75">{line2}</span>
                </span>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
